import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  forwardRef,
} from 'react';
import { Input } from 'reactstrap';
import { ImageIcon } from '@saleshandy/design-system';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment-timezone';
import { ChevronLeft, ChevronRight, CalendarEvent } from '@saleshandy/icons';
import Modal from '../design-system/components/atoms/modal';
import Button from '../design-system/ui/button';
import { Images } from '../app-constants';
import {
  dateChipTags,
  getDateTagInputLabel,
  getDateTagPrompt,
} from './helpers';
import { accessibleOnClick } from '../utils/accessible-on-click';

const DateTagEditorModal = ({
  show = false,
  onClose,
  onHide,
  hideHeader = false,
  hideFooter = false,
  modalClassName = '',
  dateTagId = '',
  onContentChange,
  editorInstance,
  ...modalProps
}: {
  show: boolean;
  onClose: () => void;
  onHide: () => void;
  hideHeader: boolean;
  hideFooter: boolean;
  modalClassName: string;
  dateTagId: string;
  onContentChange: () => void;
  editorInstance: any;
  [key: string]: any;
}) => {
  const dateTagRef = useRef<HTMLElement>(null);

  const [dateTagValue, setDateTagValue] = useState({
    tagType: '', // e.g., "CurrentDate - N"
    tagDisplayName: '', // e.g., "CurrentDate -"
    value: '', // e.g., "5" or "31/12/2024"
    hasError: false,
    isDirty: false,
  });

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const getMonth = (date: Date): string => moment(date).format('MMMM YYYY');

  // Helper function to convert DD/MM/YYYY string to Date
  const parseDate = (dateStr: string): Date | null => {
    if (!dateStr || !/^\d{2}\/\d{2}\/\d{4}$/?.test(dateStr)) {
      return null;
    }
    const [day, month, year] = dateStr?.split('/')?.map(Number);
    return new Date(year, month - 1, day);
  };

  // Helper function to convert Date to DD/MM/YYYY string
  const formatDate = (date: Date | null): string => {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const validateValue = useCallback(
    (value: string, tagType: string): string => {
      if (!value?.trim()) {
        return 'Value is required';
      }

      // Validate based on tag type
      if (dateChipTags(tagType, true, false)) {
        // Should be a number
        if (!/^\d+$/.test(value.trim())) {
          return 'Please enter a valid number';
        }
      } else if (tagType === 'DaysLeftIn / DDMMYYYY') {
        // Should be a date in DD/MM/YYYY format
        if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value.trim())) {
          return 'Please enter date in DD/MM/YYYY format';
        }
      }

      return '';
    },
    [],
  );

  const handleSaveValue = useCallback(() => {
    const trimmedValue = dateTagValue?.value?.trim() || '';
    const error = validateValue(trimmedValue, dateTagValue.tagType);

    if (error) {
      setDateTagValue((prev) => ({ ...prev, hasError: true }));
      return;
    }

    if (dateTagRef?.current) {
      const element = dateTagRef.current;
      const fallbackElement = element?.querySelector('.editable-fallback');

      if (fallbackElement) {
        // For DaysLeftIn, always show the actual date value (never use CSS default)
        const isDaysLeftIn = dateTagValue.tagType === 'DaysLeftIn / DDMMYYYY';

        // Determine if this is the default value (only for numeric date tags)
        const isDefaultValue = !isDaysLeftIn && trimmedValue === '0';

        if (isDefaultValue) {
          // For default value: empty content, add default-fallback class (CSS shows "0")
          fallbackElement.innerHTML = '';
          fallbackElement.classList.add('default-fallback');
        } else {
          // For custom value or DaysLeftIn: set content, remove default-fallback class
          fallbackElement.innerHTML = trimmedValue;
          fallbackElement.classList.remove('default-fallback');
        }
      }

      // Force rerender after value changes
      setTimeout(() => {
        onContentChange();
      }, 100);
    }
    onClose();
  }, [dateTagValue, onClose, onContentChange, validateValue]);

  // Custom input component with calendar icon
  const CustomDateInput = forwardRef<
    HTMLInputElement,
    React.HTMLProps<HTMLInputElement>
  >(({ value, onClick }, ref) => (
    <div
      className="date-picker-input-container"
      {...accessibleOnClick(onClick)}
    >
      <CalendarEvent width={16} height={16} className="calendar-icon" />
      <input
        className={`form-control ${
          dateTagValue?.hasError ? 'tag-fallback-error' : ''
        }`}
        placeholder="DD/MM/YYYY"
        value={value?.toString() || ''}
        readOnly
        ref={ref}
      />
    </div>
  ));

  useEffect(() => {
    if (dateTagId) {
      const ele = document.getElementById(dateTagId);
      dateTagRef.current = ele;
      const element = dateTagRef.current;
      if (element) {
        const tagType = element.getAttribute('data-tag-type') || '';
        const tagDisplayName =
          element.querySelector('.date-tag-chip')?.textContent?.trim() || '';
        const fallbackElement = element.querySelector('.editable-fallback');
        const hasDefaultClass = fallbackElement?.classList.contains(
          'default-fallback',
        );
        const isDaysLeftIn = tagType === 'DaysLeftIn / DDMMYYYY';

        // For numeric date tags: if default-fallback class, use "0"
        // For DaysLeftIn: always use actual text content (never "0")
        let value: string;
        if (isDaysLeftIn) {
          value = fallbackElement?.textContent?.trim() || '';
        } else {
          value = hasDefaultClass
            ? '0'
            : fallbackElement?.textContent?.trim() || '0';
        }

        setDateTagValue({
          tagType,
          tagDisplayName,
          value,
          hasError: false,
          isDirty: false,
        });

        // Initialize selectedDate for date picker
        if (tagType === 'DaysLeftIn / DDMMYYYY' && value) {
          const parsedDate = parseDate(value);
          setSelectedDate(parsedDate);
        }
      }
    }
  }, [dateTagId]);

  return (
    <Modal
      show={show}
      onClose={onClose}
      onHide={onHide}
      titleContent={<h1>Add Fallback Text</h1>}
      hideHeader={hideHeader}
      hideFooter={hideFooter}
      className={modalClassName}
      {...modalProps}
    >
      <div className="merge-tag-modal-container">
        <div className="merge-tag-modal-container__header">
          <div className="merge-tag-modal-container__header-title">
            {`{{}} ${
              getDateTagInputLabel(dateTagValue?.tagType)
            }`}
          </div>
        </div>

        <div className="merge-tag-modal-container__body">
          <div className="fallback-prompt">
            <span>{getDateTagPrompt(dateTagValue?.tagType)}</span>
          </div>
        </div>

        <div className="merge-tag-modal-container__add-fallback">
          <span className="fallback-prompt">
            {/* {dateTagValue?.tagDisplayName || ''} */}
          </span>
          <div className="add-fallback-text">
            {dateTagValue?.tagType === 'DaysLeftIn / DDMMYYYY' ? (
              <div className="date-picker-wrapper">
                <DatePicker
                  selected={selectedDate}
                  onChange={(date: Date | null) => {
                    setSelectedDate(date);
                    const formattedDate = formatDate(date);
                    const error = validateValue(
                      formattedDate,
                      dateTagValue.tagType,
                    );
                    setDateTagValue({
                      ...dateTagValue,
                      value: formattedDate,
                      hasError: !!error,
                      isDirty: true,
                    });
                  }}
                  dateFormat="dd/MM/yyyy"
                  minDate={new Date()}
                  customInput={<CustomDateInput />}
                  renderCustomHeader={({
                    date,
                    decreaseMonth,
                    increaseMonth,
                    prevMonthButtonDisabled,
                    nextMonthButtonDisabled,
                  }) => (
                    <div className="pick-date-and-time-header">
                      <p>{getMonth(date)}</p>
                      <div className="pick-date-and-time-action-btns">
                        <button
                          type="button"
                          onClick={decreaseMonth}
                          disabled={prevMonthButtonDisabled}
                        >
                          <ChevronLeft />
                        </button>
                        <button
                          type="button"
                          onClick={increaseMonth}
                          disabled={nextMonthButtonDisabled}
                        >
                          <ChevronRight />
                        </button>
                      </div>
                    </div>
                  )}
                />
              </div>
            ) : (
              <Input
                type="number"
                placeholder="Type Number of Days"
                className={`${
                  dateTagValue?.hasError ? 'tag-fallback-error' : ''
                }`}
                value={dateTagValue?.value || ''}
                onChange={(e) => {
                  const newValue = e?.target?.value || '';
                  const error = validateValue(newValue, dateTagValue.tagType);
                  setDateTagValue({
                    ...dateTagValue,
                    value: newValue,
                    hasError: !!error,
                    isDirty: true,
                  });
                }}
              />
            )}
            {dateTagValue?.hasError && (
              <div className="fallback-text-error-container">
                <ImageIcon src={Images.AlertTriangleRed} alt="Alert" />
                <div className="fallback-text-error">
                  {dateTagValue?.tagType === 'DaysLeftIn / DDMMYYYY'
                    ? 'Please enter date in DD/MM/YYYY format (e.g., 31/12/2024)'
                    : 'Enter a Valid Input'}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="merge-tag-modal-container__footer">
          <Button
            className="merge-tag-modal-container__footer-cancel-button"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            disabled={dateTagValue?.hasError || !dateTagValue?.value?.trim()}
            className="merge-tag-modal-container__footer-submit-button"
            onClick={handleSaveValue}
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default React.memo(DateTagEditorModal);
